/**
 * <SurfaceScaffold /> — CD R11 Finding #9.
 *
 * Fixes the slot order so a coherent product stops feeling assembled. Six
 * Phase 11+ surfaces were each built independently → drift in KPI-strip
 * layout, input-panel ordering, caveat-banner placement, pin-affordance
 * position, citation-footer style. Individually minor; collectively they
 * undermine the "one product" read.
 *
 * Canonical slot order (Decision-support surfaces especially):
 *
 *   1. Header           — §7.x + title + (optional) subtitle
 *   2. Honesty rail     — the surface's primary caveat (use <HonestyRail />)
 *   3. KPI strip        — the headline numbers (operator sees the punchline
 *                          BEFORE the controls — decision-support flow)
 *   4. Inputs panel     — controls that drive the KPIs
 *   5. Body             — tables, charts, narrative
 *   6. Citation footer  — source attribution
 *
 * All slots are optional — pass `undefined` (or simply omit) to suppress.
 * The scaffold renders the `<div className="surface">` wrapper + header
 * so surface bodies don't repeat the chrome.
 *
 * Migration path: existing surfaces wrap their JSX in this scaffold,
 * deleting their own `<div className="surface">` + `<div className="surface__header">`
 * blocks. Citations migrate from a free-form footer panel to the `citations`
 * slot. Honesty rails migrate from bespoke banners to <HonestyRail />.
 */

import type { ReactNode } from 'react';

interface SurfaceScaffoldProps {
  /** The §7.x section anchor. Rendered in the header. */
  section: string;
  /** The surface title. Rendered in the header. */
  title: string;
  /** Optional subtitle / one-line description under the title. */
  subtitle?: ReactNode;
  /** The surface's primary caveat. Recommended: <HonestyRail variant=... />. */
  honestyRail?: ReactNode;
  /** Headline KPI strip — the punchline numbers operators read first. */
  kpis?: ReactNode;
  /** Input controls panel — pathogen + region + sliders + toggles. */
  inputs?: ReactNode;
  /** Main body — tables, charts, narrative panels. */
  children?: ReactNode;
  /** Citation footer — sources, attribution, methodology references. */
  citations?: ReactNode;
}

export function SurfaceScaffold({
  section, title, subtitle, honestyRail, kpis, inputs, children, citations,
}: SurfaceScaffoldProps) {
  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">{section}</span>
        <h1 className="surface__title">{title}</h1>
      </div>
      {subtitle && <div className="surface__sub">{subtitle}</div>}
      {honestyRail}
      {kpis}
      {inputs}
      {children}
      {citations && (
        <div
          style={{
            marginTop: 'var(--bm-space-5)',
            paddingTop: 'var(--bm-space-3)',
            borderTop: '1px solid var(--bm-border)',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, lineHeight: 1.6,
            color: 'var(--bm-text-subtle)',
          }}
        >
          <span style={{
            display: 'inline-block', marginRight: 8,
            fontSize: 9, fontWeight: 700, letterSpacing: '.6px',
            textTransform: 'uppercase' as const,
            color: 'var(--bm-text-muted)',
          }}>
            Sources
          </span>
          {citations}
        </div>
      )}
    </div>
  );
}
